import { reaction } from "mobx";
import { makeImmutable } from "../core";
import { LocalizationProvider } from "../locale";
import { ContactDetails, DisabledCoupon, EnabledCoupon, FirstOrderDiscount, FulfillmentInstructions, OrderLine, ProductInstanceParser, PurchaseOrder, Quantity, } from "../ordering";
import { CreatorRootScope } from "../ordering/ParameterScope/CreatorRootScope";
import { OrderRootScope } from "../ordering/ParameterScope/OrderRootScope";
import { PRestaurantParser } from "../parsing";
import { ConsentsFormControler, ContactFormController, FODFormController, FulfillmentFormController, InvoiceFormControler, PCart, PCheckout, PFulfillmentTime, POrderDetails, PProductInstanceCreator, PPromotionScheme, PaymentFormControler, } from "../presentation";
import { PPurchaseOrder } from "../presentation/PPurchaseOrder";
import { CheckoutInfoService, CheckoutService, GeocodingService, OrderingAvailabilityService, RestaumaticRPCService, ValidatePhoneNumberService, WebGeolocationService, } from "../services";
import { CartStorageManager, CompositeStorageManager, ConsentsStoreManager, ContactDetailsStorageManager, FulfillmentInstructionsAfterPurchaseStorageManager, FulfillmentInstructionsStorageManager, PromotionStorageManager, StreetAddressStorageManager, } from "../storageManagers";
import { ApartmentInfoStorageManager } from "../storageManagers/ApartmentInfoStorageManager";
import { InvoiceDetailsStorageManager } from "../storageManagers/InvoiceDetailsStorageManager";
export class RestaumaticClient {
    constructor(params) {
        this.salesChannel = params.salesChannel;
        this.isQrWaiterOrder = params.isQrWaiterOrder;
        this.dineInLocationNumber = params.dineInLocationNumber;
        this.validatePhoneService = params.validatePhoneService;
        this.geocodingService = params.geocodingService;
        this.geolocationService = params.geolocationService;
        this.country = params.country;
        this.localizationProvider = params.localizationProvider;
        this.checkoutService = params.checkoutService;
        this.checkoutInfoService = params.checkoutInfoService;
        this.orderingAvailabilityService = params.orderingAvailabilityService;
        this.rpcService = params.rpcService;
        this.menuConsumer = params.menuConsumer;
        makeImmutable(this);
    }
    static create(params) {
        const rpcService = new RestaumaticRPCService({ url: params.rpcUrl });
        const localizationProvider = new LocalizationProvider({
            locale: params.locale,
            fallbackLocale: params.fallbackLocale,
            hrkDualPricesEnabled: params.hrkDualPricesEnabled,
            currency: params.currency,
        });
        const checkoutInfoService = new CheckoutInfoService({
            localizationProvider,
            rpcService,
            isApplePaySupported: params.isApplePaySupported,
        });
        const checkoutService = new CheckoutService({ rpcService });
        const geocodingService = new GeocodingService({
            rpcService,
        });
        const geolocationService = new WebGeolocationService();
        const validatePhoneService = new ValidatePhoneNumberService({
            rpcService,
        });
        const orderingAvailabilityService = new OrderingAvailabilityService({
            rpcService,
        });
        return new RestaumaticClient({
            salesChannel: params.salesChannel,
            isQrWaiterOrder: params.menuConsumer === "QrWaiter",
            dineInLocationNumber: params.dineInLocationNumber,
            validatePhoneService,
            geocodingService,
            geolocationService,
            country: params.country,
            localizationProvider,
            checkoutService,
            checkoutInfoService,
            orderingAvailabilityService,
            rpcService,
            menuConsumer: params.menuConsumer,
        });
    }
    createPurchaseOrder(restaurant) {
        const purchaseOrder = PurchaseOrder.empty({
            restaurant,
            origin: this.salesChannel,
            isQrWaiterOrder: this.isQrWaiterOrder,
            dineInLocationNumber: this.dineInLocationNumber,
            geocodingService: this.geocodingService,
            geolocationService: this.geolocationService,
            validatePhoneService: this.validatePhoneService,
            country: this.country,
        });
        return purchaseOrder;
    }
    // TODO Refactor order deserialization
    restoreOrder(params) {
        let cartStorageManager = null;
        // FIXME Refactor
        reaction(() => params.purchaseOrder.payload.checkoutData, (payloadCheckoutData) => {
            if (cartStorageManager !== null) {
                cartStorageManager.writeCart({
                    couponCode: null,
                    extraDiscount: null,
                    items: payloadCheckoutData.map((itemData) => ({
                        ...itemData,
                        key: itemData.productKey,
                    })),
                    promotionalItems: [],
                    restaurantId: params.restaurant.id.value,
                });
            }
        }, { fireImmediately: true });
        const fulfillmentInstructionsStorageManager = new FulfillmentInstructionsStorageManager(params.purchaseOrder.fulfillmentInstructions);
        // FIXME Refactor
        reaction(() => params.purchaseOrder.fulfillmentInstructions.checkoutData, () => {
            fulfillmentInstructionsStorageManager.store();
        });
        const streetAddressStorageManager = new StreetAddressStorageManager(this.country);
        reaction(() => {
            var _a;
            return (_a = params.purchaseOrder.fulfillmentInstructions.streetAddress) === null || _a === void 0 ? void 0 : _a.checkoutData;
        }, () => {
            if (params.purchaseOrder.fulfillmentInstructions.streetAddress !== null) {
                streetAddressStorageManager.store(params.purchaseOrder.fulfillmentInstructions.streetAddress);
            }
        });
        const apartmentInfoStorageManager = new ApartmentInfoStorageManager(this.country);
        reaction(() => {
            var _a;
            return (_a = params.purchaseOrder.fulfillmentInstructions.apartmentInfo) === null || _a === void 0 ? void 0 : _a.checkoutData;
        }, () => {
            if (params.purchaseOrder.fulfillmentInstructions.apartmentInfo !== null) {
                apartmentInfoStorageManager.store(params.purchaseOrder.fulfillmentInstructions.apartmentInfo);
            }
        });
        const contactDatailsStorageManager = new ContactDetailsStorageManager(this.validatePhoneService);
        reaction(() => params.purchaseOrder.contactDetails.checkoutData, () => {
            contactDatailsStorageManager.store(params.purchaseOrder.contactDetails);
        });
        const invoiceDetailsStorageManager = new InvoiceDetailsStorageManager();
        reaction(() => { var _a; return (_a = params.purchaseOrder.invoiceDetails) === null || _a === void 0 ? void 0 : _a.checkoutData; }, () => {
            invoiceDetailsStorageManager.store(params.purchaseOrder.invoiceDetails);
        });
        const promotionStorageManager = new PromotionStorageManager({
            promotionScheme: params.purchaseOrder.promotionScheme,
            coupon: params.coupon,
        });
        // FIXME Refactor
        reaction(() => params.purchaseOrder.promotionScheme.checkoutData, () => {
            promotionStorageManager.store();
        });
        reaction(() => params.purchaseOrder.fulfillmentInstructions.paymentMedium, (paymentMedium) => {
            FulfillmentInstructions.persistPaymentMedium(paymentMedium);
        });
        reaction(() => params.purchaseOrder.fulfillmentInstructions.fulfillmentMethod, (fulfillmentMethodType) => {
            FulfillmentInstructions.persistFulfillmentMethodType(fulfillmentMethodType);
        });
        cartStorageManager = new CartStorageManager();
        const cartData = cartStorageManager.readCart();
        if (cartData === null) {
            return;
        }
        if (cartData.restaurantId !== params.restaurant.id.value) {
            throw new Error("Cart restaurant id deserialization mismatch");
        }
        const productInstanceParser = new ProductInstanceParser({
            menu: params.restaurant.menu,
        });
        for (const serializedOrderLine of cartData.items) {
            const { key, quantity } = serializedOrderLine;
            if (key.tag !== "MenuProduct") {
                throw new Error("Unsupported deserialization product kind");
            }
            const productInstanceCreator = productInstanceParser.parseCreator(key.contents, params.rootScope);
            if (productInstanceCreator === null) {
                continue;
            }
            const orderLine = new OrderLine({
                productInstance: productInstanceCreator.instance,
                quantity: Quantity.fromNumber(quantity),
            });
            params.purchaseOrder.addOrderLine(orderLine);
        }
        promotionStorageManager.restore();
        fulfillmentInstructionsStorageManager.restore();
    }
    async createCheckout(menuResponse) {
        const restaurant = await new PRestaurantParser({
            input: menuResponse,
            localizationProvider: this.localizationProvider,
            rpcService: this.rpcService,
            checkoutInfoService: this.checkoutInfoService,
            orderingAvailabilityService: this.orderingAvailabilityService,
            appOrigin: this.menuConsumer,
        }).parse();
        const purchaseOrder = this.createPurchaseOrder(restaurant.domain);
        // TODO Refactor coupon creation
        const coupon = !restaurant.enableCoupons || this.isQrWaiterOrder
            ? new DisabledCoupon()
            : new EnabledCoupon({
                promotionService: restaurant.promotionService,
                promotionScheme: purchaseOrder.promotionScheme,
            });
        const rootScope = new OrderRootScope({
            restaurant: restaurant.domain,
            purchaseOrder: purchaseOrder,
        });
        this.restoreOrder({
            restaurant: restaurant.domain,
            purchaseOrder,
            coupon,
            rootScope,
        });
        const fulfillmentTime = PFulfillmentTime.create({
            purchaseOrder,
            restaurant: restaurant.domain,
        });
        const promotionScheme = new PPromotionScheme({
            localizationProvider: this.localizationProvider,
            restaurant,
            domain: purchaseOrder.promotionScheme,
            coupon,
        });
        const orderDetails = new POrderDetails({
            restaurant,
            orderReceiver: purchaseOrder.orderReceiver,
            geocodingService: this.geocodingService,
            fulfillmentTime,
            fulfillmentInstructions: purchaseOrder.fulfillmentInstructions,
            promotionScheme,
        });
        const consentsFormControler = new ConsentsFormControler({
            consents: purchaseOrder.consents,
        });
        const contactFormControler = ContactFormController.fromContactDetails(purchaseOrder.contactDetails);
        const invoiceFormControler = new InvoiceFormControler({
            orderReceiver: purchaseOrder.orderReceiver,
        });
        const fulfillmentFormControler = FulfillmentFormController.create({
            fulfillmentInstructions: purchaseOrder.fulfillmentInstructions,
            fulfillmentTime,
            geocodingService: this.geocodingService,
            geolocationService: this.geolocationService,
        });
        const paymentFormControler = new PaymentFormControler({
            paymentMediums: restaurant.paymentMediums,
            fulfillmentInstructions: purchaseOrder.fulfillmentInstructions,
        });
        const storageManager = new CompositeStorageManager([
            new FulfillmentInstructionsAfterPurchaseStorageManager(purchaseOrder.fulfillmentInstructions),
            new ConsentsStoreManager(consentsFormControler),
        ]);
        const pPurchaseOrder = new PPurchaseOrder({
            domain: purchaseOrder,
            restaurant,
            localizationProvider: this.localizationProvider,
        });
        const checkout = new PCheckout({
            purchaseOrder: pPurchaseOrder,
            restaurant,
            checkoutService: this.checkoutService,
            localizationProvider: this.localizationProvider,
            orderDetails,
            fulfillmentFormControler,
            paymentFormControler,
            contactFormControler,
            consentsFormControler,
            invoiceFormControler,
            storageManager,
            locale: this.localizationProvider.locale,
            placeOrderCallback(res) {
                // Internal redirect
                if (res.tag === "Redirect" || res.tag === "ExternalRedirect") {
                    window.location.href = res.url;
                }
            },
        });
        //checkout.touch();
        return checkout;
    }
    async createCart(menuResponse) {
        var _a;
        const restaurant = await new PRestaurantParser({
            input: menuResponse,
            localizationProvider: this.localizationProvider,
            rpcService: this.rpcService,
            checkoutInfoService: this.checkoutInfoService,
            orderingAvailabilityService: this.orderingAvailabilityService,
            appOrigin: this.menuConsumer,
        }).parse();
        const purchaseOrder = this.createPurchaseOrder(restaurant.domain);
        // TODO Refactor coupon creation
        const coupon = !restaurant.enableCoupons || this.isQrWaiterOrder
            ? new DisabledCoupon()
            : new EnabledCoupon({
                promotionService: restaurant.promotionService,
                promotionScheme: purchaseOrder.promotionScheme,
            });
        const rootScope = new OrderRootScope({
            restaurant: restaurant.domain,
            purchaseOrder: purchaseOrder,
        });
        this.restoreOrder({
            restaurant: restaurant.domain,
            purchaseOrder,
            coupon,
            rootScope,
        });
        const fulfillmentTime = PFulfillmentTime.create({
            purchaseOrder,
            restaurant: restaurant.domain,
        });
        const promotionScheme = new PPromotionScheme({
            localizationProvider: this.localizationProvider,
            restaurant,
            domain: purchaseOrder.promotionScheme,
            coupon,
        });
        const orderDetails = new POrderDetails({
            restaurant,
            orderReceiver: purchaseOrder.orderReceiver,
            geocodingService: this.geocodingService,
            fulfillmentTime,
            fulfillmentInstructions: purchaseOrder.fulfillmentInstructions,
            promotionScheme,
        });
        const fulfillmentFormControler = FulfillmentFormController.create({
            fulfillmentInstructions: purchaseOrder.fulfillmentInstructions,
            fulfillmentTime,
            geocodingService: this.geocodingService,
            geolocationService: this.geolocationService,
        });
        const contactDetailsStorageManager = new ContactDetailsStorageManager(this.validatePhoneService);
        const contactDetails = (_a = contactDetailsStorageManager.restore()) !== null && _a !== void 0 ? _a : ContactDetails.empty({
            isQrWaiterOrder: this.isQrWaiterOrder,
            validatePhoneService: this.validatePhoneService,
        });
        const firstOrderDiscount = FirstOrderDiscount.create({
            restaurantId: restaurant.id,
            validatePhoneService: this.validatePhoneService,
            checkoutService: this.checkoutService,
            contactDetails,
            restaurantEnableFod: restaurant.enableFod,
            isQrWaiterOrder: this.isQrWaiterOrder,
        });
        const fodFormController = FODFormController.create(firstOrderDiscount);
        const pPurchaseOrder = new PPurchaseOrder({
            domain: purchaseOrder,
            restaurant,
            localizationProvider: this.localizationProvider,
        });
        const cart = new PCart({
            purchaseOrder: pPurchaseOrder,
            restaurant,
            localizationProvider: this.localizationProvider,
            orderDetails,
            firstOrderDiscount,
            fodFormController,
            fulfillmentFormControler,
        });
        return { cart, restaurant, rootScope };
    }
    createCreator(restaurant, productKey) {
        const productInstanceParser = new ProductInstanceParser({
            menu: restaurant.domain.menu,
        });
        const creatorRootScope = new CreatorRootScope({
            restaurant: restaurant.domain,
        });
        const productInstanceCreator = productInstanceParser.parseCreator(productKey, creatorRootScope);
        if (productInstanceCreator === null) {
            return null;
        }
        const creator = PProductInstanceCreator.create({
            domain: productInstanceCreator,
            localizationProvider: this.localizationProvider,
            restaurant,
        });
        return creator;
    }
}
